import React, { useEffect, useState, useCallback } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Award, Plus, Trash2, RefreshCw, Save, Calculator, AlertTriangle,
  Sparkles, Calendar, Loader2, ChevronUp, ChevronDown, RotateCcw,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Error";
  if (typeof d === "string") return d;
  if (typeof d === "object") return d.message_ar || d.message_en || d.msg || JSON.stringify(d);
  return String(d);
};

const DEFAULT_TIER = { name_ar: "جديد", name_en: "New", threshold: 0, multiplier: 1.0, color: "#94a3b8" };

export default function LoyaltySettings() {
  const { lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const isAr = lang === "ar";

  const [cfg, setCfg] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalcing, setRecalcing] = useState(false);
  const [expiringReport, setExpiringReport] = useState(null);
  const [showExpiring, setShowExpiring] = useState(false);

  // Calculator state
  const [simAmount, setSimAmount] = useState(500);
  const [simRedeem, setSimRedeem] = useState(0);
  const [simTier, setSimTier] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, d] = await Promise.all([
        api.get("/loyalty/settings"),
        api.get("/loyalty/settings/defaults"),
      ]);
      setCfg(r.data);
      setDefaults(d.data);
      setSimTier(r.data?.tiers?.[0]?.name_en || "");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/loyalty/settings", cfg);
      setCfg(r.data);
      toast.success(isAr ? "تم حفظ إعدادات الولاء" : "Loyalty settings saved");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const resetToDefaults = async () => {
    if (!defaults) return;
    const ok = await confirm({
      title: isAr
        ? "هل تريد إعادة جميع القيم للوضع الافتراضي؟ ستحتاج للضغط على «حفظ» للتطبيق."
        : "Reset everything to factory defaults? You'll still need to click Save to apply.",
      confirmLabel: isAr ? "تأكيد" : "Confirm",
      cancelLabel: isAr ? "إلغاء" : "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    setCfg({ ...defaults });
    setSimTier(defaults.tiers?.[0]?.name_en || "");
    toast.info(isAr ? "تم استعادة الافتراضي — لا تنسَ الحفظ" : "Defaults restored — remember to save");
  };

  const recalculate = async () => {
    const ok = await confirm({
      title: isAr
        ? "ستتم إعادة حساب شريحة جميع العملاء حسب الحدود الجديدة. متابعة؟"
        : "All customer tiers will be recalculated using the new thresholds. Continue?",
      confirmLabel: isAr ? "تأكيد" : "Confirm",
      cancelLabel: isAr ? "إلغاء" : "Cancel",
      variant: "primary",
    });
    if (!ok) return;
    setRecalcing(true);
    try {
      const r = await api.post("/loyalty/settings/recalculate-tiers");
      toast.success(isAr
        ? `تمت إعادة حساب ${r.data.recalculated} عميل`
        : `Recalculated ${r.data.recalculated} customers`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRecalcing(false); }
  };

  const loadExpiring = async () => {
    try {
      const r = await api.get("/loyalty/expiring-points");
      setExpiringReport(r.data);
      setShowExpiring(true);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addTier = () => {
    const tiers = [...(cfg.tiers || []), { ...DEFAULT_TIER, threshold: (cfg.tiers?.length ? Math.max(...cfg.tiers.map(t => t.threshold)) + 1000 : 0) }];
    setCfg({ ...cfg, tiers });
  };
  const removeTier = (i) => {
    if ((cfg.tiers?.length || 0) <= 1) {
      toast.error(isAr ? "يجب أن تبقى شريحة واحدة على الأقل" : "Keep at least one tier");
      return;
    }
    const tiers = cfg.tiers.filter((_, idx) => idx !== i);
    setCfg({ ...cfg, tiers });
  };
  const moveTier = (i, dir) => {
    const tiers = [...cfg.tiers];
    const j = i + dir;
    if (j < 0 || j >= tiers.length) return;
    [tiers[i], tiers[j]] = [tiers[j], tiers[i]];
    setCfg({ ...cfg, tiers });
  };
  const updateTier = (i, key, val) => {
    const tiers = [...cfg.tiers];
    tiers[i] = { ...tiers[i], [key]: val };
    setCfg({ ...cfg, tiers });
  };

  // ── Simulator ──
  const sim = (() => {
    if (!cfg) return null;
    const tier = cfg.tiers?.find(t => t.name_en === simTier) || cfg.tiers?.[0] || { multiplier: 1 };
    const base = cfg.earn_basis === "subtotal" ? simAmount : simAmount; // for sim we treat both equally
    const earned = Math.floor(base * (cfg.earn_rate || 1) * (tier.multiplier || 1));
    const redeemValue = (simRedeem / Math.max(1, cfg.redeem_rate || 30)).toFixed(2);
    const maxRedeemValue = (simAmount * (cfg.max_redeem_percent || 100) / 100).toFixed(2);
    return { earned, redeemValue, maxRedeemValue, tier };
  })();

  if (loading || !cfg) {
    return <div className="card-surface p-10 text-center"><Loader2 className="inline animate-spin" size={24}/></div>;
  }

  return (
    <div className="space-y-5" data-testid="loyalty-settings-panel">
      {/* Header bar */}
      <div className="card-surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-heading text-xl text-[var(--text)] flex items-center gap-2">
              <Award size={20} className="text-amber-600"/>
              {isAr ? "نظام الولاء" : "Loyalty Program"}
            </h3>
            <p className="text-xs text-[var(--text-muted)] mt-1 max-w-2xl">
              {isAr
                ? "اضبط معدلات الكسب والاستبدال والشرائح بالكامل. التغييرات تُطبَّق على الفواتير الجديدة فوراً، ولإعادة حساب شرائح العملاء الحاليين استخدم زر «إعادة الحساب»."
                : "Tune earn rates, redemption, tiers, and expiry. Changes apply to new invoices instantly. Use 'Recalculate' to re-stamp existing customer tiers."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={loadExpiring} data-testid="loyalty-expiring-btn"
              className="px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 text-xs flex items-center gap-1.5">
              <Calendar size={14}/>{isAr ? "نقاط على وشك الانتهاء" : "Expiring Points"}
            </button>
            <button onClick={recalculate} disabled={recalcing} data-testid="loyalty-recalc-btn"
              className="px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 text-xs flex items-center gap-1.5 disabled:opacity-50">
              {recalcing ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
              {isAr ? "إعادة حساب الشرائح" : "Recalculate Tiers"}
            </button>
            <button onClick={resetToDefaults} data-testid="loyalty-reset-btn"
              className="px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 text-xs flex items-center gap-1.5">
              <RotateCcw size={14}/>{isAr ? "افتراضي" : "Defaults"}
            </button>
            <button onClick={save} disabled={saving} data-testid="loyalty-save-btn"
              className="btn-gold px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
              {isAr ? "حفظ" : "Save"}
            </button>
          </div>
        </div>

        {/* Master enabled toggle */}
        <div className="mt-5 flex items-center justify-between gap-4 border border-stone-200 rounded-lg p-3">
          <div>
            <div className="text-sm font-semibold text-[var(--text)]">{isAr ? "تفعيل نظام الولاء" : "Enable Loyalty Program"}</div>
            <div className="text-xs text-[var(--text-muted)] mt-1">
              {isAr ? "عند الإيقاف، لن يكسب العملاء نقاطاً ولن يمكن استبدالها في POS." : "When disabled, customers won't earn points and POS redemption is blocked."}
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input type="checkbox" className="sr-only peer" checked={!!cfg.enabled}
              data-testid="loyalty-enabled-toggle"
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}/>
            <div className="w-11 h-6 bg-stone-200 peer-checked:bg-emerald-500 rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:start-0.5 after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5 rtl:peer-checked:after:-translate-x-5"/>
          </label>
        </div>
      </div>

      {/* Earning */}
      <div className="card-surface p-5 space-y-4">
        <h4 className="font-semibold text-[var(--text)] flex items-center gap-2">
          <Sparkles size={16} className="text-amber-600"/>{isAr ? "قواعد الاكتساب" : "Earning Rules"}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label={isAr ? "نقاط لكل 1 ر.ق" : "Points per 1 QAR"}
            hint={isAr ? "المعدل الأساسي قبل تطبيق مضاعف الشريحة" : "Base rate before tier multiplier"}>
            <input type="number" min="0" step="0.1" value={cfg.earn_rate}
              data-testid="loyalty-earn-rate"
              onChange={(e) => setCfg({ ...cfg, earn_rate: parseFloat(e.target.value) || 0 })}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm num-display"/>
          </Field>
          <Field label={isAr ? "أساس الحساب" : "Calculation basis"}
            hint={isAr ? "هل تُحتسب النقاط على المبلغ قبل أو بعد الضريبة؟" : "Earn on amount before or after tax?"}>
            <select value={cfg.earn_basis} onChange={(e) => setCfg({ ...cfg, earn_basis: e.target.value })}
              data-testid="loyalty-earn-basis"
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm">
              <option value="total">{isAr ? "الإجمالي بعد الضريبة" : "Total (after tax)"}</option>
              <option value="subtotal">{isAr ? "Subtotal (قبل الضريبة)" : "Subtotal (before tax)"}</option>
            </select>
          </Field>
          <Field label={isAr ? "احتساب على المبلغ بعد الخصم" : "Earn on discounted amount"}
            hint={isAr ? "إذا كان مفعّلاً، النقاط تُحسب على المبلغ النهائي بعد الخصومات" : "If enabled, points are computed on the post-discount amount"}>
            <Toggle checked={!!cfg.earn_on_discounted}
              testid="loyalty-earn-on-disc"
              onChange={(v) => setCfg({ ...cfg, earn_on_discounted: v })}/>
          </Field>
        </div>
      </div>

      {/* Redemption */}
      <div className="card-surface p-5 space-y-4">
        <h4 className="font-semibold text-[var(--text)] flex items-center gap-2">
          <RefreshCw size={16} className="text-amber-600"/>{isAr ? "قواعد الاستبدال" : "Redemption Rules"}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label={isAr ? "كم نقطة = 1 ر.ق" : "Points per 1 QAR redeemed"}
            hint={isAr ? "كلما زاد الرقم، قلّت قيمة النقطة" : "Higher number = lower point value"}>
            <input type="number" min="1" value={cfg.redeem_rate}
              data-testid="loyalty-redeem-rate"
              onChange={(e) => setCfg({ ...cfg, redeem_rate: parseInt(e.target.value) || 1 })}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm num-display"/>
          </Field>
          <Field label={isAr ? "حد أدنى للاستبدال (نقاط)" : "Minimum redemption (points)"}
            hint={isAr ? "يمنع الاستبدال بأقل من هذا الرقم" : "Block redemptions below this amount"}>
            <input type="number" min="0" value={cfg.min_redeem_points}
              data-testid="loyalty-min-redeem"
              onChange={(e) => setCfg({ ...cfg, min_redeem_points: parseInt(e.target.value) || 0 })}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm num-display"/>
          </Field>
          <Field label={isAr ? "أقصى نسبة من الفاتورة (%)" : "Max % of invoice"}
            hint={isAr ? "أقصى نسبة من قيمة الفاتورة يمكن دفعها بالنقاط" : "Cap redemption value to this % of subtotal"}>
            <input type="number" min="0" max="100" value={cfg.max_redeem_percent}
              data-testid="loyalty-max-pct"
              onChange={(e) => setCfg({ ...cfg, max_redeem_percent: parseInt(e.target.value) || 0 })}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm num-display"/>
          </Field>
        </div>
      </div>

      {/* Tiers editor */}
      <div className="card-surface p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-[var(--text)] flex items-center gap-2">
            <Award size={16} className="text-amber-600"/>{isAr ? "الشرائح" : "Tiers"}
          </h4>
          <button onClick={addTier} data-testid="loyalty-add-tier"
            className="text-xs px-3 py-1.5 rounded border border-stone-200 hover:bg-stone-50 flex items-center gap-1">
            <Plus size={14}/>{isAr ? "إضافة شريحة" : "Add Tier"}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="loyalty-tiers-table">
            <thead className="text-xs text-stone-500 uppercase">
              <tr>
                <th className="text-start py-2 px-2">{isAr ? "الاسم (عربي)" : "Name (AR)"}</th>
                <th className="text-start py-2 px-2">{isAr ? "الاسم (إنجليزي)" : "Name (EN)"}</th>
                <th className="text-start py-2 px-2">{isAr ? "حد أدنى (نقاط)" : "Min Points"}</th>
                <th className="text-start py-2 px-2">{isAr ? "المضاعف ×" : "Multiplier ×"}</th>
                <th className="text-start py-2 px-2">{isAr ? "اللون" : "Color"}</th>
                <th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {cfg.tiers?.map((t, i) => (
                <tr key={i} className="border-t border-stone-100" data-testid={`tier-row-${i}`}>
                  <td className="py-2 px-2">
                    <input value={t.name_ar} onChange={(e) => updateTier(i, "name_ar", e.target.value)}
                      className="border border-stone-200 rounded px-2 py-1 text-sm w-full"/>
                  </td>
                  <td className="py-2 px-2">
                    <input value={t.name_en} onChange={(e) => updateTier(i, "name_en", e.target.value)}
                      className="border border-stone-200 rounded px-2 py-1 text-sm w-full"/>
                  </td>
                  <td className="py-2 px-2">
                    <input type="number" min="0" value={t.threshold}
                      onChange={(e) => updateTier(i, "threshold", parseInt(e.target.value) || 0)}
                      className="border border-stone-200 rounded px-2 py-1 text-sm w-24 num-display"/>
                  </td>
                  <td className="py-2 px-2">
                    <input type="number" min="0" step="0.1" value={t.multiplier}
                      onChange={(e) => updateTier(i, "multiplier", parseFloat(e.target.value) || 0)}
                      className="border border-stone-200 rounded px-2 py-1 text-sm w-20 num-display"/>
                  </td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-2">
                      <input type="color" value={t.color || "#94a3b8"}
                        onChange={(e) => updateTier(i, "color", e.target.value)}
                        className="w-8 h-8 rounded cursor-pointer border border-stone-200"/>
                      <span className="text-xs text-stone-500 font-mono">{t.color}</span>
                    </div>
                  </td>
                  <td className="py-2 px-2 text-end">
                    <div className="inline-flex gap-1">
                      <button onClick={() => moveTier(i, -1)} className="p-1 rounded hover:bg-stone-100 text-stone-500" title="Up"><ChevronUp size={14}/></button>
                      <button onClick={() => moveTier(i, 1)} className="p-1 rounded hover:bg-stone-100 text-stone-500" title="Down"><ChevronDown size={14}/></button>
                      <button onClick={() => removeTier(i)} data-testid={`tier-remove-${i}`}
                        className="p-1 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-[11px] text-stone-500">
          {isAr
            ? "ترتيب الشرائح حسب «حد أدنى من النقاط» تصاعدياً. سيتم الترتيب تلقائياً عند الحفظ."
            : "Tiers are ordered ascending by min-points. Will be auto-sorted on save."}
        </div>
      </div>

      {/* Expiry */}
      <div className="card-surface p-5 space-y-4">
        <h4 className="font-semibold text-[var(--text)] flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-600"/>{isAr ? "انتهاء صلاحية النقاط" : "Points Expiry"}
        </h4>
        <div className="flex items-center justify-between border border-stone-200 rounded-lg p-3">
          <div>
            <div className="text-sm font-semibold">{isAr ? "تفعيل انتهاء الصلاحية" : "Enable expiry"}</div>
            <div className="text-xs text-stone-500 mt-1">
              {isAr
                ? "عند التفعيل، تنتهي صلاحية النقاط بعد المدة المحددة من آخر فاتورة"
                : "When enabled, points expire after the specified period since the last earning invoice"}
            </div>
          </div>
          <Toggle checked={!!cfg.expiry_enabled}
            testid="loyalty-expiry-toggle"
            onChange={(v) => setCfg({ ...cfg, expiry_enabled: v })}/>
        </div>
        {cfg.expiry_enabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={isAr ? "مدة الصلاحية (أيام)" : "Expiry period (days)"}
              hint={isAr ? "365 يوم = سنة، 180 = 6 أشهر" : "365 = 1 year, 180 = 6 months"}>
              <input type="number" min="1" value={cfg.expiry_days}
                data-testid="loyalty-expiry-days"
                onChange={(e) => setCfg({ ...cfg, expiry_days: parseInt(e.target.value) || 1 })}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm num-display"/>
            </Field>
            <Field label={isAr ? "أيام التحذير قبل الانتهاء" : "Warning days before expiry"}
              hint={isAr ? "يظهر العملاء في تقرير «النقاط على وشك الانتهاء»" : "Customers within this window appear in the marketing report"}>
              <input type="number" min="1" value={cfg.expiry_warning_days}
                data-testid="loyalty-warning-days"
                onChange={(e) => setCfg({ ...cfg, expiry_warning_days: parseInt(e.target.value) || 1 })}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm num-display"/>
            </Field>
          </div>
        )}
      </div>

      {/* Simulator */}
      <div className="card-surface p-5">
        <h4 className="font-semibold text-[var(--text)] flex items-center gap-2 mb-3">
          <Calculator size={16} className="text-amber-600"/>{isAr ? "محاكاة فاتورة" : "Invoice Simulator"}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <Field label={isAr ? "قيمة الفاتورة (ر.ق)" : "Invoice amount (QAR)"}>
            <input type="number" min="0" value={simAmount}
              data-testid="sim-amount"
              onChange={(e) => setSimAmount(parseFloat(e.target.value) || 0)}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm num-display"/>
          </Field>
          <Field label={isAr ? "نقاط للاستبدال" : "Points to redeem"}>
            <input type="number" min="0" value={simRedeem}
              data-testid="sim-redeem"
              onChange={(e) => setSimRedeem(parseInt(e.target.value) || 0)}
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm num-display"/>
          </Field>
          <Field label={isAr ? "شريحة العميل" : "Customer tier"}>
            <select value={simTier} onChange={(e) => setSimTier(e.target.value)}
              data-testid="sim-tier"
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm">
              {cfg.tiers?.map(t => (
                <option key={t.name_en} value={t.name_en}>
                  {isAr ? `${t.name_ar} (×${t.multiplier})` : `${t.name_en} (×${t.multiplier})`}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {sim && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-center" data-testid="sim-results">
            <ResultCard label={isAr ? "نقاط مكتسبة" : "Points earned"}
              value={sim.earned.toLocaleString()} color="emerald"/>
            <ResultCard label={isAr ? "قيمة الاستبدال (ر.ق)" : "Redemption value (QAR)"}
              value={sim.redeemValue} color="amber"/>
            <ResultCard label={isAr ? "الحد الأقصى المسموح (ر.ق)" : "Max allowed value (QAR)"}
              value={sim.maxRedeemValue} color="blue"/>
          </div>
        )}
      </div>

      {/* Expiring report modal */}
      {showExpiring && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="expiring-modal" onMouseDown={(e) => onBackdropMouseDown(e, () => setShowExpiring(false))}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-6 max-h-[90vh] flex flex-col" onMouseDown={onModalPanelMouseDown}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-heading text-xl flex items-center gap-2">
                  <AlertTriangle size={20} className="text-amber-600"/>
                  {isAr ? "نقاط على وشك الانتهاء" : "Points About to Expire"}
                </h3>
                <p className="text-xs text-stone-500 mt-1">
                  {expiringReport?.enabled === false
                    ? (isAr ? "ميزة انتهاء الصلاحية معطّلة." : "Expiry feature is disabled.")
                    : (isAr
                      ? `عملاء ستنتهي نقاطهم خلال ${expiringReport?.warning_days} يوم`
                      : `Customers whose points expire within ${expiringReport?.warning_days} days`)}
                </p>
              </div>
              <button onClick={() => setShowExpiring(false)} className="text-stone-400 hover:text-stone-700">✕</button>
            </div>
            <div className="overflow-y-auto flex-1">
              {expiringReport?.enabled === false ? (
                <div className="p-8 text-center text-stone-500 text-sm">
                  {isAr ? "فعّل خاصية انتهاء النقاط أعلاه لاستخدام هذا التقرير." : "Enable points expiry above to use this report."}
                </div>
              ) : expiringReport?.count === 0 ? (
                <div className="p-8 text-center text-stone-500 text-sm">
                  {isAr ? "لا يوجد عملاء بنقاط على وشك الانتهاء حالياً." : "No customers with expiring points right now."}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-xs text-stone-500 uppercase">
                    <tr>
                      <th className="text-start p-2">{isAr ? "العميل" : "Customer"}</th>
                      <th className="text-start p-2">{isAr ? "الهاتف" : "Phone"}</th>
                      <th className="text-start p-2">{isAr ? "النقاط" : "Points"}</th>
                      <th className="text-start p-2">{isAr ? "الشريحة" : "Tier"}</th>
                      <th className="text-start p-2">{isAr ? "ينتهي خلال" : "Expires in"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expiringReport?.items?.map(r => (
                      <tr key={r.customer_id} className="border-t border-stone-100">
                        <td className="p-2 font-semibold">{r.name}</td>
                        <td className="p-2 text-stone-600 font-mono text-xs">{r.phone}</td>
                        <td className="p-2 num-display font-bold text-amber-700">{r.loyalty_points.toLocaleString()}</td>
                        <td className="p-2 text-xs">{r.tier}</td>
                        <td className="p-2">
                          <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${
                            r.days_until_expiry <= 7 ? "bg-rose-100 text-rose-700" :
                            r.days_until_expiry <= 14 ? "bg-amber-100 text-amber-700" :
                            "bg-stone-100 text-stone-700"
                          }`}>
                            {r.days_until_expiry} {isAr ? "يوم" : "days"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="mt-4 text-end">
              <button onClick={() => setShowExpiring(false)} className="px-4 py-2 rounded-lg text-sm bg-stone-100 hover:bg-stone-200">
                {isAr ? "إغلاق" : "Close"}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}

// ── Small helpers ──
const Field = ({ label, hint, children }) => (
  <div>
    <label className="text-xs text-stone-500 mb-1 block">{label}</label>
    {children}
    {hint && <div className="text-[10px] text-stone-400 mt-1 leading-relaxed">{hint}</div>}
  </div>
);

const Toggle = ({ checked, onChange, testid }) => (
  <label className="relative inline-flex items-center cursor-pointer shrink-0">
    <input type="checkbox" className="sr-only peer" checked={checked}
      data-testid={testid}
      onChange={(e) => onChange(e.target.checked)}/>
    <div className="w-11 h-6 bg-stone-200 peer-checked:bg-emerald-500 rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:start-0.5 after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5 rtl:peer-checked:after:-translate-x-5"/>
  </label>
);

const ResultCard = ({ label, value, color }) => {
  const cls = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber:   "bg-amber-50 border-amber-200 text-amber-800",
    blue:    "bg-blue-50 border-blue-200 text-blue-800",
  }[color] || "bg-stone-50 border-stone-200 text-stone-700";
  return (
    <div className={`border rounded-lg p-3 ${cls}`}>
      <div className="text-[11px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1 num-display">{value}</div>
    </div>
  );
};
